import { useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { GitMerge, Loader2, Sparkles } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
  invalidateAfterMerge,
  useContactCleanupStatus,
  useContactDuplicates,
  useMergeContacts,
  useStartContactCleanup,
} from '@/hooks/useContacts';
import { formatDateTime } from '@/utils/format';

import type { ContactCleanupStatus, ContactDuplicateGroup } from '@repo/shared-types';

interface DuplicateContactsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const GroupCard = ({
  group,
  onMerged,
  disabled,
}: {
  group: ContactDuplicateGroup;
  onMerged: () => void;
  disabled?: boolean;
}) => {
  const [primaryId, setPrimaryId] = useState(group.suggested_primary_id);
  const mergeContacts = useMergeContacts();
  const { toast } = useToast();

  const handleMerge = () => {
    const duplicateIds = group.contacts.map((c) => c.id).filter((id) => id !== primaryId);
    mergeContacts.mutate(
      { primary_id: primaryId, duplicate_ids: duplicateIds },
      {
        onSuccess: (result) => {
          toast({
            title: 'Contacts merged',
            description: `${result.merged_contact_ids.length} duplicate(s) merged${
              result.conversations_merged > 0
                ? `, ${result.conversations_merged} chat(s) combined`
                : ''
            }.`,
          });
          // No explicit refetch: invalidateAfterMerge already invalidates the
          // ['contacts'] prefix, which re-fetches the duplicates query.
        },
        onError: (error) => {
          const status = (error as { response?: { status?: number } }).response?.status;
          if (status === 404) {
            // The group was merged elsewhere (background cleanup, another tab).
            toast({
              title: 'Already merged',
              description: 'This group was merged by another process — refreshing the list.',
            });
            onMerged();
            return;
          }
          toast({
            title: 'Merge failed',
            description: error instanceof Error ? error.message : 'Unknown error',
            variant: 'destructive',
          });
        },
      },
    );
  };

  return (
    <div className="rounded-lg border p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex gap-2">
          {group.match_types.map((type) => (
            <Badge key={type} variant="secondary">
              Same {type}
            </Badge>
          ))}
        </div>
        <Button size="sm" onClick={handleMerge} disabled={disabled || mergeContacts.isPending}>
          {mergeContacts.isPending ? (
            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
          ) : (
            <GitMerge className="mr-1 h-4 w-4" />
          )}
          Merge group
        </Button>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-muted-foreground text-left text-xs uppercase">
            <th className="px-2 py-1">Keep</th>
            <th className="px-2 py-1">Name</th>
            <th className="px-2 py-1">Phone</th>
            <th className="px-2 py-1">Email</th>
            <th className="px-2 py-1">Calls</th>
            <th className="px-2 py-1">Chats</th>
            <th className="px-2 py-1">Created</th>
          </tr>
        </thead>
        <tbody>
          {group.contacts.map((contact) => (
            <tr key={contact.id} className={contact.id === primaryId ? 'bg-muted/50' : ''}>
              <td className="px-2 py-1">
                <input
                  type="radio"
                  name={`primary-${group.suggested_primary_id}`}
                  checked={contact.id === primaryId}
                  onChange={() => setPrimaryId(contact.id)}
                  aria-label={`Keep ${contact.name || contact.phone || contact.email}`}
                />
              </td>
              <td className="px-2 py-1 font-medium">{contact.name || '-'}</td>
              <td className="px-2 py-1">{contact.phone || '-'}</td>
              <td className="px-2 py-1">{contact.email || '-'}</td>
              <td className="px-2 py-1">{contact.calls_count}</td>
              <td className="px-2 py-1">{contact.conversations_count}</td>
              <td className="px-2 py-1">{formatDateTime(contact.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const cleanupResultSummary = (status: ContactCleanupStatus): string => {
  const result = status.result;
  if (!result) return 'Done.';
  const parts = [
    `${result.contacts_merged} duplicate(s) merged`,
    `${result.conversations_merged} chat(s) combined`,
  ];
  if (result.contacts_normalized > 0) parts.unshift(`${result.contacts_normalized} normalized`);
  if (result.secondary_numbers_recovered) {
    parts.push(`${result.secondary_numbers_recovered} lost number(s) recovered`);
  }
  return `${parts.join(', ')}.`;
};

export const DuplicateContactsDialog = ({ open, onOpenChange }: DuplicateContactsDialogProps) => {
  const { data: groups, isLoading, refetch } = useContactDuplicates(open);
  // Always enabled (not gated on `open`): the dialog stays mounted with the
  // contacts page, so a running job keeps being polled after the dialog
  // closes and the completion effect below still refreshes stale lists.
  const { data: cleanup } = useContactCleanupStatus(true);
  const startCleanup = useStartContactCleanup();
  const { toast } = useToast();
  const qc = useQueryClient();

  const cleanupRunning = cleanup?.status === 'running';
  const previousStatus = useRef(cleanup?.status);

  // Announce completion/failure once when the background job finishes.
  useEffect(() => {
    if (previousStatus.current === 'running' && cleanup?.status === 'completed') {
      toast({ title: 'Cleanup complete', description: cleanupResultSummary(cleanup) });
      invalidateAfterMerge(qc);
      refetch();
    }
    if (previousStatus.current === 'running' && cleanup?.status === 'failed') {
      toast({
        title: 'Cleanup failed',
        description: cleanup.error ?? 'Unknown error',
        variant: 'destructive',
      });
    }
    previousStatus.current = cleanup?.status;
  }, [cleanup, qc, refetch, toast]);

  const handleStart = (mode: 'merge-only' | 'full') => {
    startCleanup.mutate(mode, {
      onError: (error) => {
        toast({
          title: 'Could not start cleanup',
          description: error instanceof Error ? error.message : 'Unknown error',
          variant: 'destructive',
        });
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Duplicate contacts</DialogTitle>
          <DialogDescription>
            Contacts that share the same phone line (ignoring +44 / 0 prefixes and spaces) or the
            same email (ignoring case). Merging keeps all calls, chats, campaign history, and phone
            numbers on the contact you choose to keep. Everything is rule-based — no AI decides what
            gets merged.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => handleStart('merge-only')}
            disabled={cleanupRunning || startCleanup.isPending}
          >
            <GitMerge className="mr-1 h-4 w-4" />
            Merge all duplicates
          </Button>
          <Button
            variant="secondary"
            onClick={() => handleStart('full')}
            disabled={cleanupRunning || startCleanup.isPending}
          >
            <Sparkles className="mr-1 h-4 w-4" />
            Full clean-up
          </Button>
        </div>

        {cleanupRunning && (
          <div className="bg-muted/50 flex items-center gap-3 rounded-lg border p-3 text-sm">
            <Loader2 className="text-muted-foreground h-4 w-4 shrink-0 animate-spin" />
            <div>
              <p className="font-medium">
                {cleanup?.mode === 'full' ? 'Full clean-up' : 'Merging duplicates'} in progress —
                you can close this dialog, it keeps running.
              </p>
              <p className="text-muted-foreground">
                {cleanup?.stage}
                {cleanup?.groups_total
                  ? ` · ${cleanup.groups_processed ?? 0}/${cleanup.groups_total} groups`
                  : ''}
              </p>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="text-muted-foreground py-8 text-center text-sm">
            Scanning for duplicates...
          </div>
        ) : !groups || groups.length === 0 ? (
          <div className="text-muted-foreground rounded-lg border border-dashed p-6 text-center text-sm">
            No duplicate contacts found.
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map((group) => (
              <GroupCard
                key={group.contacts.map((c) => c.id).join('-')}
                group={group}
                // A running cleanup merges these same groups — a click mid-run
                // would 404 on already-deleted contacts.
                disabled={cleanupRunning}
                onMerged={() => refetch()}
              />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
